<!-- B02 机构资料维护（仅审通后）+ 扩品类申请 -->
<template>
  <div v-loading="loading">
    <el-card shadow="never">
      <template #header>
        <div class="card-header">
          <span>机构信息</span>
          <el-tag v-if="profile?.status" type="success" size="small">{{ profile.status }}</el-tag>
        </div>
      </template>

      <el-alert
        v-if="profile && profile.depositSatisfied === false"
        class="mb-16px"
        :closable="false"
        show-icon
        title="保证金已无可用余额（余额为 0 或已全部冻结），无法内派师傅。请线下打款后联系平台确认入账。"
        type="error"
      />
      <el-alert
        v-else-if="profile && profile.depositNeedReplenish"
        class="mb-16px"
        :closable="false"
        show-icon
        title="可用保证金低于应缴门槛，仍可派单，建议线下补缴后联系平台入账。"
        type="warning"
      />

      <el-descriptions v-if="profile" :column="2" border class="mb-16px deposit-block">
        <el-descriptions-item label="应缴承诺金">
          {{ formatYuan(profile.depositRequired) }} 元
        </el-descriptions-item>
        <el-descriptions-item label="账户余额">
          {{ formatYuan(profile.depositBalance) }} 元
        </el-descriptions-item>
        <el-descriptions-item label="冻结金额">
          {{ formatYuan(profile.depositFrozen) }} 元
        </el-descriptions-item>
        <el-descriptions-item label="可用保证金">
          {{ formatYuan(profile.depositAvailable) }} 元
        </el-descriptions-item>
        <el-descriptions-item label="派单状态">
          <el-tag :type="profile.depositSatisfied ? 'success' : 'danger'" size="small">
            {{ profile.depositSatisfied ? '可派单' : '不可派单' }}
          </el-tag>
        </el-descriptions-item>
      </el-descriptions>

      <el-form ref="formRef" :model="formData" :rules="formRules" label-width="120px" class="profile-form">
        <el-form-item label="机构名称" prop="name">
          <el-input v-model="formData.name" maxlength="100" />
        </el-form-item>
        <el-form-item label="信用代码">
          <el-input :model-value="profile?.licenseNo" disabled />
          <div class="form-tip">统一社会信用代码审通后不可修改</div>
        </el-form-item>
        <el-form-item label="营业执照">
          <UploadImg v-model="formData.licenseUrl" directory="o2o/institution/license" />
        </el-form-item>
        <el-form-item label="法人姓名" prop="legalPerson">
          <el-input v-model="formData.legalPerson" maxlength="32" />
        </el-form-item>
        <el-form-item label="身份证正面">
          <UploadImg v-model="formData.idCardFront" directory="o2o/institution/idcard" />
        </el-form-item>
        <el-form-item label="身份证反面">
          <UploadImg v-model="formData.idCardBack" directory="o2o/institution/idcard" />
        </el-form-item>
        <el-form-item label="服务品类">
          <div class="category-block">
            <el-tag v-for="name in categoryNames" :key="name" class="mr-8px mb-8px">{{ name }}</el-tag>
            <div v-if="!categoryNames.length" class="form-tip">暂无品类信息</div>
            <div class="category-actions">
              <el-button
                type="primary"
                link
                :disabled="latestApply?.auditStatus === 0"
                @click="openCategoryDialog"
              >
                申请增加品类
              </el-button>
              <el-tag v-if="latestApply?.auditStatus === 0" type="warning" size="small" class="ml-8px">
                扩品审核中（{{ latestApply.applyNo }}）
              </el-tag>
              <el-tag v-else-if="latestApply?.auditStatus === 2" type="danger" size="small" class="ml-8px">
                上次驳回：{{ latestApply.auditRemark || '无备注' }}
              </el-tag>
            </div>
            <div class="form-tip">
              扩品通过后仅更新机构经营范围；员工仍须在「员工管理 → 能力/排班」绑定新品类后才能接单。
            </div>
          </div>
        </el-form-item>
        <el-form-item label="联系手机" prop="contactPhone">
          <el-input v-model="formData.contactPhone" maxlength="11" />
        </el-form-item>
        <el-form-item label="联系邮箱" prop="contactEmail">
          <el-input v-model="formData.contactEmail" maxlength="64" placeholder="选填，如 name@example.com" />
        </el-form-item>
        <el-form-item label="省市区" prop="areaPath">
          <el-cascader
            v-model="formData.areaPath"
            class="!w-full"
            :options="areaTree"
            :props="storeAreaCascaderProps"
            clearable
            filterable
            placeholder="请选择省 / 市 / 区（区可选）"
            @change="onAreaPathChange"
          />
        </el-form-item>
        <el-form-item label="详细地址" prop="address">
          <el-input v-model="formData.address" maxlength="200" show-word-limit placeholder="街道、门牌号等" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="saveLoading" @click="handleSave">保存资料</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-dialog v-model="categoryDialogVisible" title="申请增加服务品类" width="560px" destroy-on-close>
      <el-alert
        class="mb-12px"
        type="info"
        :closable="false"
        title="仅可勾选尚未开通的叶子品类；提交后由平台审核，通过后生效。"
      />
      <el-tree-select
        v-model="addCategoryKeys"
        :data="expandTreeOptions"
        :props="{ label: 'label', children: 'children', disabled: 'disabled', value: 'value' }"
        node-key="value"
        multiple
        show-checkbox
        filterable
        default-expand-all
        check-strictly
        class="w-full"
        placeholder="选择要新增的叶子品类"
      />
      <template #footer>
        <el-button @click="categoryDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="applyLoading" @click="submitCategoryApply">提交申请</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import type { FormInstance, FormRules } from 'element-plus'
import { UploadImg } from '@/components/UploadFile'
import { getAreaTree } from '@/api/merchant/system/area'
import {
  createO2oInstitutionCategoryApply,
  getO2oInstitutionApplyCategoryTree,
  getO2oInstitutionCategoryApplyLatest,
  getO2oInstitutionProfile,
  updateO2oInstitutionProfile,
  type O2oInstitutionApplyCategoryVO,
  type O2oInstitutionCategoryApplyVO,
  type O2oInstitutionProfileVO
} from '@/api/merchant/o2o/institution'
import {
  findStoreAreaPathByNames,
  resolveStoreAreaByPath,
  storeAreaCascaderProps
} from '@/views/o2o/utils/storeArea'

defineOptions({ name: 'MerchantO2oInstitutionProfile' })

const message = useMessage()
const loading = ref(true)
const saveLoading = ref(false)
const applyLoading = ref(false)
const profile = ref<O2oInstitutionProfileVO | null>(null)
const categoryTree = ref<O2oInstitutionApplyCategoryVO[]>([])
const latestApply = ref<O2oInstitutionCategoryApplyVO | null>(null)
const formRef = ref<FormInstance>()
const categoryDialogVisible = ref(false)
const addCategoryKeys = ref<number[]>([])
const areaTree = ref<any[]>([])

const formData = reactive({
  name: '',
  licenseUrl: '',
  legalPerson: '',
  idCardFront: '',
  idCardBack: '',
  contactPhone: '',
  contactEmail: '',
  province: '',
  city: '',
  district: '',
  address: '',
  /** 仅前端级联用，提交前不传后端 */
  areaPath: undefined as number[] | undefined
})

const validateAreaPath = (_rule: unknown, value: number[] | undefined, callback: (e?: Error) => void) => {
  if (!value?.length || value.length < 2) {
    callback(new Error('请选择省市区（至少选到市）'))
    return
  }
  callback()
}

const formRules: FormRules = {
  name: [{ required: true, message: '请输入机构名称', trigger: 'blur' }],
  legalPerson: [{ required: true, message: '请输入法人姓名', trigger: 'blur' }],
  contactPhone: [
    { required: true, message: '请输入联系手机', trigger: 'blur' },
    { pattern: /^1\d{10}$/, message: '手机号格式不正确', trigger: 'blur' }
  ],
  contactEmail: [{ type: 'email', message: '请输入正确的邮箱地址', trigger: ['blur', 'change'] }],
  areaPath: [{ required: true, validator: validateAreaPath, trigger: 'change' }],
  address: [{ required: true, message: '请输入详细地址', trigger: 'blur' }]
}

const ensureAreaTree = async () => {
  if (areaTree.value.length) return
  areaTree.value = (await getAreaTree()) || []
}

const applyAreaNames = (path?: number[]) => {
  const names = resolveStoreAreaByPath(areaTree.value, path)
  formData.province = names.province
  formData.city = names.city
  formData.district = names.district
}

const onAreaPathChange = (path?: number[]) => {
  applyAreaNames(path)
}

const syncAreaPathFromNames = () => {
  const path = findStoreAreaPathByNames(
    areaTree.value,
    formData.province,
    formData.city,
    formData.district
  )
  formData.areaPath = path
  if (path) applyAreaNames(path)
}

const ownedCategoryIds = computed(() => new Set(profile.value?.serviceCategoryIds || []))

const categoryNameMap = computed(() => {
  const map = new Map<number, string>()
  const walk = (nodes?: O2oInstitutionApplyCategoryVO[]) => {
    nodes?.forEach((node) => {
      if (node.id != null) {
        map.set(node.id, node.name)
      }
      walk(node.children)
    })
  }
  walk(categoryTree.value)
  return map
})

const categoryNames = computed(() => {
  const ids = profile.value?.serviceCategoryIds || []
  return ids.map((id) => categoryNameMap.value.get(id) || `品类#${id}`)
})

type TreeOpt = { value: number; label: string; disabled?: boolean; children?: TreeOpt[] }

const expandTreeOptions = computed(() => {
  const owned = ownedCategoryIds.value
  const walk = (nodes?: O2oInstitutionApplyCategoryVO[]): TreeOpt[] => {
    if (!nodes?.length) return []
    return nodes.map((n) => {
      const children = walk(n.children)
      const isLeaf = !children.length
      const disabled = isLeaf ? owned.has(n.id) : children.every((c) => c.disabled)
      return {
        value: n.id,
        label: n.name + (isLeaf && owned.has(n.id) ? '（已开通）' : ''),
        disabled,
        children: children.length ? children : undefined
      }
    })
  }
  return walk(categoryTree.value)
})

function formatYuan(value?: number) {
  if (value == null) return '—'
  return Number(value).toFixed(2)
}

function collectLeafIds(keys: number[]): number[] {
  const owned = ownedCategoryIds.value
  const leafIds: number[] = []
  const walk = (nodes?: O2oInstitutionApplyCategoryVO[]) => {
    nodes?.forEach((n) => {
      const hasChildren = !!n.children?.length
      if (!hasChildren && keys.includes(n.id) && !owned.has(n.id)) {
        leafIds.push(n.id)
      }
      walk(n.children)
    })
  }
  walk(categoryTree.value)
  return leafIds
}

function openCategoryDialog() {
  if (latestApply.value?.auditStatus === 0) {
    message.warning('已有待审核的扩品类申请')
    return
  }
  addCategoryKeys.value = []
  categoryDialogVisible.value = true
}

async function submitCategoryApply() {
  const addCategoryIds = collectLeafIds(addCategoryKeys.value)
  if (!addCategoryIds.length) {
    message.warning('请选择至少一个尚未开通的叶子品类')
    return
  }
  applyLoading.value = true
  try {
    await createO2oInstitutionCategoryApply({ addCategoryIds })
    message.success('扩品类申请已提交，请等待平台审核')
    categoryDialogVisible.value = false
    latestApply.value = await getO2oInstitutionCategoryApplyLatest()
  } finally {
    applyLoading.value = false
  }
}

async function loadData() {
  loading.value = true
  try {
    const [profileData, tree, latest] = await Promise.all([
      getO2oInstitutionProfile(),
      getO2oInstitutionApplyCategoryTree(),
      getO2oInstitutionCategoryApplyLatest(),
      ensureAreaTree()
    ])
    profile.value = profileData
    categoryTree.value = tree || []
    latestApply.value = latest || null
    formData.name = profileData.name
    formData.licenseUrl = profileData.licenseUrl || ''
    formData.legalPerson = profileData.legalPerson
    formData.idCardFront = profileData.idCardFront || ''
    formData.idCardBack = profileData.idCardBack || ''
    formData.contactPhone = profileData.contactPhone
    formData.contactEmail = profileData.contactEmail || ''
    formData.province = profileData.province || ''
    formData.city = profileData.city || ''
    formData.district = profileData.district || ''
    formData.address = profileData.address
    formData.areaPath = undefined
    syncAreaPathFromNames()
    if ((profileData.province || profileData.city) && !formData.areaPath?.length) {
      message.warning('原省市区无法匹配地区库，请重新选择')
    }
  } finally {
    loading.value = false
  }
}

async function handleSave() {
  applyAreaNames(formData.areaPath)
  await formRef.value?.validate()
  saveLoading.value = true
  try {
    const { areaPath: _areaPath, ...payload } = formData
    await updateO2oInstitutionProfile({ ...payload })
    message.success('机构资料已保存')
    await loadData()
  } finally {
    saveLoading.value = false
  }
}

onMounted(() => loadData())
</script>

<style lang="scss" scoped>
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.profile-form {
  max-width: 720px;
}

.deposit-block {
  max-width: 720px;
}

.form-tip {
  width: 100%;
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}

.category-block {
  width: 100%;
}

.category-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 8px;
}

.ml-8px {
  margin-left: 8px;
}
</style>
